import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  DatePicker, 
  Input, 
  Table, 
  Button, 
  message, 
  Space,
  Typography,
  Tag
} from 'antd';
import { SearchOutlined, EditOutlined } from '@ant-design/icons';
import userService from '../../services/userService';
import moment from 'moment';

const { RangePicker } = DatePicker;
const { Search } = Input;
const { Title } = Typography;

const UsersByDateRange = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [userList, setUserList] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
  });
  const [filters, setFilters] = useState({
    searchText: '',
    dateRange: null,
  });

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (text, record) => (
        <span>{`${record.user_firstname || ''} ${record.user_lastname || ''}`.trim()}</span>
      ),
      sorter: (a, b) => {
        const nameA = `${a.user_firstname || ''} ${a.user_lastname || ''}`.trim();
        const nameB = `${b.user_firstname || ''} ${b.user_lastname || ''}`.trim();
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Email',
      dataIndex: 'user_email',
      key: 'user_email',
      sorter: (a, b) => a.user_email?.localeCompare(b.user_email),
    },
    {
      title: 'Mobile Number',
      dataIndex: 'user_mobile_number',
      key: 'user_mobile_number',
      sorter: (a, b) => a.user_mobile_number?.localeCompare(b.user_mobile_number),
    },
    {
      title: 'Verified',
      dataIndex: 'user_is_verified',
      key: 'user_is_verified',
      render: (text) => (
        <Tag color={text === 'Y' ? 'green' : 'red'}>
          {text === 'Y' ? 'Yes' : 'No'}
        </Tag>
      ),
      filters: [
        { text: 'Yes', value: 'Y' },
        { text: 'No', value: 'N' },
      ],
      onFilter: (value, record) => record.user_is_verified === value,
    },
    {
      title: 'Role',
      dataIndex: 'role_name',
      key: 'role_name',
      render: (text) => (
        <Tag color={text === 'admin' ? 'blue' : 'default'}>
          {text === 'admin' ? 'Admin' : text || 'User'}
        </Tag>
      ),
      filters: [
        { text: 'Admin', value: 'admin' },
        { text: 'User', value: 'user' },
      ],
      onFilter: (value, record) => {
        if (value === 'admin') return record.role_name === 'admin';
        if (value === 'user') return record.role_name !== 'admin';
        return true;
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => text ? moment(text).format('YYYY-MM-DD HH:mm') : '-',
      sorter: (a, b) => moment(a.created_at).unix() - moment(b.created_at).unix(),
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (text, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/users/edit/${record.user_id}`)}
            size="small"
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  const getUsers = async (params = {}) => {
    try {
      setLoading(true);
      const queryParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        ...params,
      };

      console.log('Fetching users with params:', queryParams);
      const response = await userService.getUsers(queryParams);
      console.log('API Response:', response);

      const { data } = response;

      if (data && data.status) {
        setUserList(data.data.data || []);
        setPagination(prev => ({
          ...prev,
          ...data.data.pagination,
          current: data.data.pagination.currentPage,
        }));
      } else {
        console.log('API returned no data or status false:', data);
        setUserList([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      message.error('Failed to fetch users');
      setUserList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  const handleDateRangeChange = async (dates, dateStrings) => {
    if (dates && dates.length === 2) {
      setFilters(prev => ({ ...prev, dateRange: dates }));
      
      try {
        setLoading(true);
        const [startDate, endDate] = dateStrings;
        console.log('Fetching users by date range:', { startDate, endDate, filter: filters.searchText });
        
        const response = await userService.getUserByDateRange({
          startDate,
          endDate,
          filter: filters.searchText,
        });

        console.log('Date range API Response:', response);

        if (response.data && response.data.status) {
          setUserList(response.data.data || []);
          setPagination(prev => ({
            ...prev,
            total: response.data.data?.length || 0,
            current: 1,
          }));
        } else {
          console.log('Date range API returned no data or status false:', response.data);
          setUserList([]);
        }
      } catch (error) {
        console.error('Error fetching users by date range:', error);
        message.error('Failed to fetch users by date range');
        setUserList([]);
      } finally {
        setLoading(false);
      }
    } else {
      // Reset to all users when date range is cleared
      setFilters(prev => ({ ...prev, dateRange: null }));
      getUsers();
    }
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, searchText: value }));
    getUsers({
      search: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  const handleTableChange = (newPagination, tableFilters, sorter) => {
    const { current, pageSize } = newPagination;
    
    if (current !== pagination.current || pageSize !== pagination.pageSize) {
      setPagination(prev => ({
        ...prev,
        current,
        pageSize,
      }));
      
      getUsers({
        search: filters.searchText,
        perPage: pageSize,
        currentPage: current,
      });
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Title level={4} style={{ marginBottom: 16 }}>
            Users By Date Range
          </Title>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 16,
            flexWrap: 'wrap',
            gap: 16
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <RangePicker
                onChange={handleDateRangeChange}
                placeholder={['Start Date', 'End Date']}
                style={{ width: 300 }}
                format="YYYY-MM-DD"
                allowClear
              />
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Search
                placeholder="Search users..."
                allowClear
                style={{ width: 300 }}
                onSearch={handleSearch}
                enterButton={<SearchOutlined />}
              />
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={userList}
          rowKey="user_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          bordered
        />
      </Card>
    </div>
  );
};

export default UsersByDateRange;
